import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, Check, X, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Winback() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/winback/${token}`)
      .then((r) => setOffer(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Offer not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/winback/accept/${token}`);
      setDone(true);
      toast.success(data.message || "Welcome back!");
      setTimeout(() => navigate("/parent"), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply offer");
      setError(e?.response?.data?.detail || "Failed");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-zinc-950">
        <div className="font-display text-2xl font-extrabold">Loading offer…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-zinc-950 p-6">
        <div className="nb-card p-10 max-w-md text-center nb-shadow-xl">
          <div className="font-display text-2xl font-extrabold mb-2">Offer unavailable</div>
          <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-5">{error}</p>
          <button onClick={() => navigate("/")} className="nb-button px-5 py-3">Go home</button>
        </div>
      </div>
    );
  }

  if (done || offer?.status === "accepted") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-zinc-950 p-6">
        <div data-testid="winback-success" className="nb-card p-10 max-w-md text-center nb-shadow-xl bg-[#9DFFB0] dark:bg-[#68D391] text-black">
          <div className="w-16 h-16 mx-auto mb-4 nb-border rounded-full bg-white flex items-center justify-center">
            <Check className="w-9 h-9 text-black" strokeWidth={3} />
          </div>
          <div className="font-display text-3xl font-extrabold mb-2">Welcome back, {offer?.name?.split(" ")[0] || "friend"}!</div>
          <p className="font-medium mb-2">Your subscription stays active and {offer?.percent_off || 50}% off your next month is applied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-zinc-950 p-6">
      <div data-testid="winback-page" className="nb-card p-8 sm:p-10 max-w-lg w-full nb-shadow-xl">
        <div className="flex items-center gap-2 nb-border rounded-full px-3 py-1.5 bg-[#FFE066] w-fit mb-5 text-black">
          <Heart className="w-4 h-4" strokeWidth={3} />
          <span className="label-caps">A little gift</span>
        </div>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-3">
          {offer?.name?.split(" ")[0] ? `${offer.name.split(" ")[0]}, ` : ""}
          come back?
        </h1>
        <p className="text-zinc-700 dark:text-zinc-300 font-medium mb-6">
          We noticed you cancelled. We&apos;d love to keep helping your child crush GCSEs, here&apos;s
          <span className="nb-border rounded-lg px-2 py-0.5 mx-1 bg-[#9DFFB0] text-black font-extrabold">
            {offer?.percent_off || 50}% off
          </span>
          your next month if you stay.
        </p>

        <div className="nb-border rounded-2xl p-4 bg-zinc-50 dark:bg-zinc-800 mb-6 text-sm font-medium space-y-1">
          <div>Plan: <span className="font-bold">{offer?.plan?.replace("_", " ") || "Premium"}</span></div>
          {offer?.current_period_end && (
            <div>
              Renews on:{" "}
              <span className="font-bold">
                {new Date(offer.current_period_end).toLocaleDateString(undefined, {
                  day: "numeric", month: "long", year: "numeric"
                })}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={accept}
            disabled={applying}
            data-testid="winback-accept"
            className="nb-button px-5 py-3 flex-1 bg-[#D4C4FB] dark:bg-[#B794F4] flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4" strokeWidth={3} />
            {applying ? "Applying…" : `Claim ${offer?.percent_off || 50}% off`}
          </button>
          <button
            data-testid="winback-decline"
            onClick={() => navigate("/")}
            className="nb-button px-5 py-3 flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" /> No thanks
          </button>
        </div>
        <p className="text-xs text-zinc-500 font-medium mt-4 text-center">
          One-time discount applied to your next invoice. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
